import { Controller } from "@hotwired/stimulus"

// Search-as-you-type for the SCA clinical-code inputs (CPT/HCPCS, ICD-10-CM,
// revenue codes). Mounted once on the claim card alongside
// sca-code-descriptions; individual inputs opt in with data-actions and a
// data-sca-code-kind attribute, so rows added by dynamic-rows work without
// re-connecting anything.
//
// Queries are POSTed as `clinical_code` — never in a URL — so partial codes
// and diagnosis keywords stay out of access/proxy logs (Rails additionally
// filters the clinical_code param). Picking a result fills the input and
// dispatches `change`, which the existing sca-code-descriptions controller
// picks up to render the authoritative server-side description.
//
// Requests are tracked with a generation token: each new search (from any
// input), blur, and close bumps the generation, and a response only renders if
// its generation is still current AND it originated from the input the
// operator is still editing. This prevents an out-of-order or late response
// for input A from opening a menu — and routing a pick — into input A after
// the operator has moved on to input B.
let listboxSequence = 0

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.menu = null
    this.activeInput = null
    this.highlighted = -1
    this.debounce = null
    this.generation = 0
    // Document-unique id for this controller's listbox, referenced by the
    // active input's aria-controls/aria-activedescendant while open.
    listboxSequence += 1
    this.listboxId = `sca-code-search-listbox-${listboxSequence}`
    this.boundClose = this.closeFromOutside.bind(this)
    document.addEventListener("pointerdown", this.boundClose)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.boundClose)
    this.close()
  }

  search(event) {
    const input = event.currentTarget
    clearTimeout(this.debounce)
    const query = input.value.trim()
    // Any new keystroke invalidates whatever request is currently in flight,
    // even one from a different input.
    this.generation += 1
    this.searchInput = input
    if (query.length < 2) {
      this.close()
      return
    }
    const generation = this.generation
    this.debounce = setTimeout(() => this.fetchResults(input, query, generation), 250)
  }

  keydown(event) {
    if (!this.menu) return
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.close()
      return
    }
    const items = Array.from(this.menu.querySelectorAll("[data-code]"))
    if (items.length === 0) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const delta = event.key === "ArrowDown" ? 1 : -1
      this.highlighted = Math.min(Math.max(this.highlighted + delta, 0), items.length - 1)
      this.applyHighlight(items)
    } else if (event.key === "Enter" && this.highlighted >= 0) {
      event.preventDefault()
      this.pick(items[this.highlighted].dataset.code)
    }
  }

  blurInput() {
    // Leaving the input invalidates its in-flight request immediately; the
    // close itself is delayed so a mousedown on a menu item can complete its
    // pick first (close() bumps the generation again, which is harmless).
    this.generation += 1
    setTimeout(() => this.close(), 150)
  }

  async fetchResults(input, query, generation) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) return

    const body = new URLSearchParams()
    body.set("kind", input.dataset.scaCodeKind)
    body.set("clinical_code", query)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken
        },
        body
      })
      if (!response.ok) return
      const payload = await response.json()
      // Drop the response unless it is still the newest request, came from the
      // input the operator is still editing, and that input still holds the
      // searched query.
      if (generation !== this.generation) return
      if (input !== this.searchInput) return
      if (input.value.trim() !== query) return
      this.render(input, payload.results || [])
    } catch {
      // Code search is a convenience; manual entry remains authoritative.
    }
  }

  // The menu mounts inside the claim card's [data-sca-code-search-anchor]
  // wrapper when one exists (the scrollable claim-document body, positioned
  // relative), so an open menu scrolls with its input and can extend the
  // scrollable area instead of being clipped by the card's rounded footer.
  // Cards without the wrapper keep the original card-level mount.
  searchAnchor() {
    return this.element.querySelector("[data-sca-code-search-anchor]") || this.element
  }

  render(input, results) {
    this.close({ preserveGeneration: true })
    if (results.length === 0) return

    this.activeInput = input
    this.highlighted = -1
    this.menu = document.createElement("div")
    this.menu.id = this.listboxId
    this.menu.setAttribute("role", "listbox")
    this.menu.className =
      "absolute z-40 mt-1 max-h-64 overflow-y-auto rounded-lg border " +
      "border-support-light-300 bg-white p-1 shadow-lg"

    results.forEach(({ code, description }, index) => {
      const item = document.createElement("div")
      item.id = `${this.listboxId}-option-${index}`
      item.setAttribute("role", "option")
      item.setAttribute("aria-selected", "false")
      item.dataset.code = code
      item.className = "flex cursor-pointer items-baseline gap-3 rounded-md px-3 py-2.5 hover:bg-core-purple-200"
      const codeEl = document.createElement("span")
      codeEl.className = "min-w-[4.5rem] flex-none font-mono text-sm font-bold text-support-dark-800"
      codeEl.textContent = code
      const descEl = document.createElement("span")
      descEl.className = "min-w-0 text-sm text-support-dark-600"
      descEl.textContent = description
      item.append(codeEl, descEl)
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.pick(code)
      })
      this.menu.appendChild(item)
    })

    this.positionMenu(input)
    this.searchAnchor().appendChild(this.menu)
    this.openListbox(input)
  }

  // The anchor is position: relative, so it is the menu's offset parent and
  // anchor-relative coordinates position the menu directly under the input.
  // The panel prefers the input's width with a readable 320px floor, but both
  // width and left offset clamp to the anchor so a narrow rail (mobile, or a
  // right-edge revenue-code input) never overflows horizontally: the floor
  // only survives when the anchor has room for it.
  positionMenu(input) {
    const anchorRect = this.searchAnchor().getBoundingClientRect()
    const inputRect = input.getBoundingClientRect()
    const width = Math.min(Math.max(inputRect.width, 320), anchorRect.width)
    const left = Math.min(
      Math.max(inputRect.left - anchorRect.left, 0),
      Math.max(anchorRect.width - width, 0)
    )
    this.menu.style.left = `${left}px`
    this.menu.style.top = `${inputRect.bottom - anchorRect.top}px`
    this.menu.style.width = `${width}px`
  }

  // ARIA combobox state while the listbox is open. The inputs carry the
  // static half (role=combobox, aria-autocomplete=list) in their markup;
  // these two keep the dynamic half in sync with the menu lifecycle.
  openListbox(input) {
    input.setAttribute("aria-expanded", "true")
    input.setAttribute("aria-controls", this.listboxId)
    input.removeAttribute("aria-activedescendant")
  }

  closeListbox(input) {
    input.setAttribute("aria-expanded", "false")
    input.removeAttribute("aria-controls")
    input.removeAttribute("aria-activedescendant")
  }

  applyHighlight(items) {
    items.forEach((el, i) => {
      const highlighted = i === this.highlighted
      el.classList.toggle("bg-core-purple-200", highlighted)
      el.setAttribute("aria-selected", highlighted ? "true" : "false")
    })
    const active = items[this.highlighted]
    if (active && this.activeInput) this.activeInput.setAttribute("aria-activedescendant", active.id)
  }

  pick(code) {
    const input = this.activeInput
    this.close()
    if (!input) return
    input.value = code
    // `change` drives both the server-side description lookup
    // (sca-code-descriptions#lookup) and the reprice dirty tracking
    // (sca-reprice-lock#markDirty). Deliberately NOT dispatching `input` —
    // these inputs bind input->sca-code-search#search, so an `input` event
    // would immediately re-run the search and reopen the menu over the field.
    input.dispatchEvent(new Event("change", { bubbles: true }))
    input.focus()
  }

  closeFromOutside(event) {
    if (this.menu && !this.menu.contains(event.target) && event.target !== this.activeInput) this.close()
  }

  close({ preserveGeneration = false } = {}) {
    clearTimeout(this.debounce)
    // Invalidate any in-flight request so it can't render into a closed menu.
    if (!preserveGeneration) this.generation += 1
    if (this.menu) this.menu.remove()
    if (this.activeInput) this.closeListbox(this.activeInput)
    this.menu = null
    this.activeInput = null
    this.highlighted = -1
  }
}
